import React, { useState, useRef } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  FloatingArrow,
  useHover,
  useInteractions,
  arrow,
  Placement,
  FloatingPortal,
  useTransitionStyles,
} from "@floating-ui/react";
import styles from "./tooltip.module.css";
import { mergeRefs } from "../../../canvas-elements/task/merge-refs";

type TooltipProps = {
  text: string;
  children: React.ReactElement;
  side?: Placement;
  offset?: number;
  maxWidth?: number;
  disabled?: boolean;
};

// This is the latest and greatest version of the Tooltip component !!
// Made in the spirit of design system!
// By John !!Yes, that John !!
export function Tooltip({
  text,
  children,
  side = "top",
  maxWidth,
  disabled = false,
  offset: offsetValue = 0,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const arrowRef = useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: open && !disabled,
    onOpenChange: setOpen,
    placement: side,
    transform: false,
    middleware: [offset(offsetValue), flip(), shift({ padding: 8 }), arrow({ element: arrowRef })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 350, close: 150 },
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);
  const childRef = (children.props as React.RefAttributes<unknown>)?.ref;

  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: 200,
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
    common: ({ side }) => ({
      transformOrigin: {
        top: "bottom",
        bottom: "top",
        left: "right",
        right: "left",
      }[side],
    }),
  });

  return (
    <>
      {React.cloneElement(children, {
        ref: mergeRefs(refs.setReference, childRef),
        ...getReferenceProps(),
      })}
      {isMounted && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              ...transitionStyles,
              maxWidth: maxWidth ? `${maxWidth}px` : undefined,
            }}
            className={styles.tooltip}
            {...getFloatingProps()}
          >
            {text}
            <FloatingArrow ref={arrowRef} context={context} tipRadius={1.5} width={10} height={5} fill="#1E1E1E" />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
